import {inject, Injectable, makeStateKey, signal, TransferState} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {catchError, firstValueFrom, of} from 'rxjs';
import {createLogger} from "../../shared/helpers/logger/logger";
import {RuntimeService} from "./runtime.service";

const logger = createLogger('AuthService');

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    // Define a signal to represent the token state
    hasToken = signal<boolean>(false);
    protected apiUrl = environment.apiUrl;
    readonly #runtimeService = inject(RuntimeService);

    constructor(
        protected http: HttpClient,
        private transferState: TransferState
    ) {
        this.checkToken().then(r => r);
    }

    // Call this method after token changes, such as on login or logout
    public async updateTokenStatus(): Promise<void> {
        await this.checkToken();
    }

    // Store token and refresh token securely in IndexedDB
    async setToken(accessToken: string, refreshToken: string, expiresIn: number): Promise<void> {
        if (this.#runtimeService.isServer) return; // Ensure it's running in the browser

        const {encryptedToken, iv, key} = await this.encryptToken(accessToken);

        // Open IndexedDB and store tokens and IV
        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readwrite');
        const store = transaction.objectStore('auth');

        // Store access token, IV, and expiration time
        store.put({
            id: 'access_token',
            token: encryptedToken,
            iv: Array.from(iv),
            expires: new Date().getTime() + expiresIn * 1000,
        });

        // Store refresh token
        store.put({id: 'refresh_token', token: refreshToken});

        // Store encryption key
        await this.storeEncryptionKey(key);

        transaction.oncomplete = () => {
            this.updateTokenStatus();
        };

        transaction.onerror = (error) => {
            logger.error('Error storing token in IndexedDB', error);
        };

        db.close();
    }

    // Generic GET method
    async get<T>(
        endpoint: string,
        params?: any,
        headers?: HttpHeaders
    ): Promise<HttpResponse<T>> {
        const authHeaders = await this.createAuthHeaders(headers);
        const options = {
            params: params ? this.serializeParams(params) : undefined,
            headers: authHeaders,
            observe: 'response' as const,
        };
        const url = `${this.apiUrl}${endpoint}`;
        const isAuthenticated = await this.hasAuthToken();
        const keyString = this.generateTransferStateKey('GET', url, options.params);
        const TRANSFER_KEY = makeStateKey<{ body: T; headers: { [key: string]: string } }>(keyString);

        if (isAuthenticated) {
            return await this.fetchData<T>(url, options);
        } else {
            if (this.#runtimeService.isBrowser) {
                if (this.transferState.hasKey(TRANSFER_KEY)) {
                    const cached = this.transferState.get<{
                        body: T;
                        headers: { [key: string]: string }
                    }>(TRANSFER_KEY, null as any);
                    this.transferState.remove(TRANSFER_KEY);
                    return new HttpResponse<T>({
                        body: cached.body,
                        headers: new HttpHeaders(cached.headers),
                        status: 200,
                        statusText: 'OK',
                        url: url ?? undefined,
                    });
                } else {
                    return await this.fetchData<T>(url, options);
                }
            } else if (this.#runtimeService.isServer) {
                const response = await this.fetchData<T>(url, options);
                const headersToStore = {
                    'X-Pagination-Total-Count': response.headers.get('X-Pagination-Total-Count') || '',
                    'X-Pagination-Page-Count': response.headers.get('X-Pagination-Page-Count') || '',
                    'X-Pagination-Current-Page': response.headers.get('X-Pagination-Current-Page') || '',
                };
                this.transferState.set(TRANSFER_KEY, {body: response.body as T, headers: headersToStore});
                return response;
            } else {
                return await this.fetchData<T>(url, options);
            }
        }
    }

    // Generic POST method
    async post<T>(endpoint: string, body: any, headers?: HttpHeaders): Promise<T> {
        const authHeaders = await this.createAuthHeaders(headers);
        try {
            return await firstValueFrom(
                this.http.post<T>(`${this.apiUrl}${endpoint}`, body, {headers: authHeaders})
            );
        } catch (error: unknown) {
            this.handleError(error);
            throw error;
        }
    }

    // PUT method
    async put<T>(endpoint: string, body: any, headers?: HttpHeaders): Promise<T> {
        const authHeaders = await this.createAuthHeaders(headers);
        try {
            return await firstValueFrom(
                this.http.put<T>(`${this.apiUrl}${endpoint}`, body, {headers: authHeaders})
            );
        } catch (error: unknown) {
            this.handleError(error);
            throw error;
        }
    }

    // DELETE method
    async delete<T>(endpoint: string, body?: any, headers?: HttpHeaders): Promise<T> {
        const authHeaders = await this.createAuthHeaders(headers);
        try {
            return await firstValueFrom(
                this.http.delete<T>(`${this.apiUrl}${endpoint}`, {headers: authHeaders, body})
            );
        } catch (error: unknown) {
            this.handleError(error);
            throw error;
        }
    }

    // Retrieve token from IndexedDB
    async getToken(): Promise<string | null> {
        if (this.#runtimeService.isServer) return null; // Ensure running in browser

        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readonly');
        const store = transaction.objectStore('auth');

        const tokenEntry = await new Promise<any>((resolve, reject) => {
            const request = store.get('access_token');
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(null);
        });

        db.close();

        if (!tokenEntry || new Date().getTime() >= tokenEntry.expires) {
            return null; // Token expired
        }

        // Retrieve encryption key from IndexedDB
        const key = await this.retrieveEncryptionKey();
        if (key && tokenEntry.token) {
            const iv = new Uint8Array(tokenEntry.iv);
            return await this.decryptToken(tokenEntry.token, key, iv);
        }

        return null;
    }

    async storePhoneNumber(phoneNumber: string): Promise<void> {
        // Open the database and start the transaction
        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readwrite');
        const store = transaction.objectStore('auth');

        // Store the encrypted data
        store.put({
            id: 'identifier',
            value: phoneNumber,
        });

        // Ensure we close the database after transaction completes
        return new Promise<void>((resolve, reject) => {
            transaction.oncomplete = () => {
                db.close();
                resolve();
            };

            transaction.onerror = (error) => {
                logger.error('Failed to store Phone Number:', error);
                db.close();
                reject(error);
            };
        });
    }

    async storeFirebaseToken(fid: string | null): Promise<void> {
        // Open the database and start the transaction
        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readwrite');
        const store = transaction.objectStore('auth');

        // Store the encrypted data
        store.put({
            id: 'fid',
            value: fid,
        });

        // Ensure we close the database after transaction completes
        return new Promise<void>((resolve, reject) => {
            transaction.oncomplete = () => {
                db.close();
                resolve();
            };

            transaction.onerror = (error) => {
                logger.error('Failed to store FID:', error);
                db.close();
                reject(error);
            };
        });
    }

    async getPhoneNumber(): Promise<string | null> {
        if (this.#runtimeService.isServer) return null; // Ensure running in browser

        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readonly');
        const store = transaction.objectStore('auth');

        const tokenEntry = await new Promise<any>((resolve, reject) => {
            const request = store.get('identifier');
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(null);
        });

        db.close();
        return tokenEntry && tokenEntry.value ? tokenEntry.value : '';
    }

    async getFirebaseToken(): Promise<string | null> {
        if (this.#runtimeService.isServer) return null; // Ensure running in browser

        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readonly');
        const store = transaction.objectStore('auth');

        const tokenEntry = await new Promise<any>((resolve, reject) => {
            const request = store.get('fid');
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(null);
        });

        db.close();
        return tokenEntry && tokenEntry.value ? tokenEntry.value : '';
    }

    // Create authorization header, with token refresh if necessary
    protected async createAuthHeaders(additionalHeaders?: HttpHeaders): Promise<HttpHeaders> {
        let headers = additionalHeaders || new HttpHeaders();
        const token = await this.getToken();
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
            return headers;
        } else {
            const newToken = await this.refreshToken();
            if (newToken) {
                headers = headers.set('Authorization', `Bearer ${newToken}`);
            }
            return headers;
        }
    }

    // Handle API errors
    protected handleError(error: unknown): void {
        logger.error('API Error:', error);
        if (error instanceof HttpErrorResponse) {
            logger.error('HTTP Error Status:', error.status);
            logger.error('HTTP Error Body:', error.error);
        } else {
            logger.error('An unexpected error occurred:', error);
        }
    }

    // Separate function to handle data fetching with error handling
    private async fetchData<T>(
        url: string,
        options: any
    ): Promise<HttpResponse<T>> {
        try {
            return await firstValueFrom(this.http.get<T>(url, options)) as HttpResponse<T>;
        } catch (error: unknown) {
            this.handleError(error);
            throw error;
        }
    }

// Checks if there is a valid auth token in IndexedDB
    private async hasAuthToken(): Promise<boolean> {
        const token = await this.getToken(); // Reuse the getToken method in your service
        return token !== null;
    }

    private generateTransferStateKey(method: string, url: string, params?: HttpParams): string {
        let key = method + ':' + url;
        if (params) {
            key += `?${params.toString()}`;
        }
        // Replace any non-alphanumeric characters to ensure the key is valid
        return key.replace(/[^a-zA-Z0-9]/g, '_');
    }

    // Method to get token from IndexedDB or localStorage (depending on your setup)
    private async checkToken(): Promise<void> {
        const token = await this.getToken();
        this.hasToken.set(token !== null);
    }

    private serializeParams(params: any, prefix = ''): HttpParams {
        let httpParams = new HttpParams();

        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const value = params[key];

                if (value === null || value === undefined || value === '') {
                    continue;
                }

                const paramKey = prefix ? `${prefix}[${key}]` : key;

                if (typeof value === 'object' && !Array.isArray(value)) {
                    httpParams = this.mergeHttpParams(
                        httpParams,
                        this.serializeParams(value, paramKey)
                    );
                } else if (Array.isArray(value)) {
                    value.forEach((item: any) => {
                        httpParams = httpParams.append(`${paramKey}[]`, item);
                    });
                } else {
                    httpParams = httpParams.append(paramKey, value);
                }
            }
        }
        return httpParams;
    }

    private mergeHttpParams(params1: HttpParams, params2: HttpParams): HttpParams {
        let combinedParams = params1;
        params2.keys().forEach((key) => {
            const values = params2.getAll(key);
            if (values) {
                values.forEach((value) => {
                    combinedParams = combinedParams.append(key, value);
                });
            }
        });
        return combinedParams;
    }

    // Encrypt token using Web Crypto API
    private async encryptToken(token: string): Promise<{ encryptedToken: string; iv: Uint8Array; key: CryptoKey }> {
        const enc = new TextEncoder();
        const key = await window.crypto.subtle.generateKey(
            {name: 'AES-GCM', length: 256},
            true,
            ['encrypt', 'decrypt']
        );

        const iv = window.crypto.getRandomValues(new Uint8Array(12)); // Initialization vector
        const encrypted = await window.crypto.subtle.encrypt({name: 'AES-GCM', iv}, key, enc.encode(token));

        const encryptedToken = Array.from(new Uint8Array(encrypted))
            .map((b) => String.fromCharCode(b))
            .join('');

        return {encryptedToken: btoa(encryptedToken), iv, key};
    }

    // Decrypt token when needed
    private async decryptToken(encryptedToken: string, key: CryptoKey, iv: Uint8Array): Promise<string> {
        try {
            const encTokenBytes = Uint8Array.from(atob(encryptedToken), (c) => c.charCodeAt(0));

            const decryptedBuffer = await window.crypto.subtle.decrypt({name: 'AES-GCM', iv}, key, encTokenBytes);

            const dec = new TextDecoder();
            return dec.decode(decryptedBuffer);
        } catch (error) {
            logger.error('Decryption failed:', error);
            return ''; // Handle decryption errors
        }
    }

    // Store encryption key in IndexedDB
    private async storeEncryptionKey(key: CryptoKey): Promise<void> {
        const exportedKey = await window.crypto.subtle.exportKey('raw', key);

        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readwrite');
        const store = transaction.objectStore('auth');

        store.put({
            id: 'encryption_key',
            key: Array.from(new Uint8Array(exportedKey)),
        });

        transaction.oncomplete = () => {
        };

        transaction.onerror = (error) => {
            logger.error('Failed to store encryption key:', error);
        };

        db.close();
    }

    // Retrieve encryption key from IndexedDB
    private async retrieveEncryptionKey(): Promise<CryptoKey | null> {
        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readonly');
        const store = transaction.objectStore('auth');

        const keyEntry = await new Promise<any>((resolve, reject) => {
            const request = store.get('encryption_key');
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(null);
        });

        db.close();

        if (keyEntry) {
            const keyBytes = new Uint8Array(keyEntry.key);
            return window.crypto.subtle.importKey('raw', keyBytes, {name: 'AES-GCM'}, true, ['encrypt', 'decrypt']);
        }

        return null;
    }

    // Open IndexedDB and create object stores if necessary
    private async openIndexedDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('authDB', 1);
            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('auth')) {
                    db.createObjectStore('auth', {keyPath: 'id'});
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => reject(event);
        });
    }

    // Get refresh token from IndexedDB
    private async getRefreshToken(): Promise<string | null> {
        if (this.#runtimeService.isServer) return null;

        const db = await this.openIndexedDB();
        const transaction = db.transaction('auth', 'readonly');
        const store = transaction.objectStore('auth');

        const refreshTokenEntry = await new Promise<any>((resolve, reject) => {
            const request = store.get('refresh_token');
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(null);
        });

        db.close();
        return refreshTokenEntry ? refreshTokenEntry.token : null;
    }

    // Refresh token logic
    private async refreshToken(): Promise<string | null> {
        const refreshToken = await this.getRefreshToken();
        if (!refreshToken) {
            return null;
        } else {
            const body = {
                refresh_token: refreshToken,
                grant_type: 'refresh_token',
                scopes: "app"
            };

            const basicAuth = 'Basic ' + environment.authBasic;

            const headers = new HttpHeaders({
                Authorization: basicAuth,
                'Content-Type': 'application/json',
            });
            try {
                const response: any = await firstValueFrom(
                    this.http.post(`${environment.authUrl}`, body, {headers}).pipe(
                        catchError((error: any) => {
                            logger.error('Token refresh error:', error);
                            // Optionally, handle logout or redirect to login page
                            return of(null);
                        })
                    )
                );
                if (response && response.access_token) {
                    const newAccessToken = response.access_token;
                    const newExpiresIn = response.expires_in;
                    const newRefreshToken = response.refresh_token || refreshToken;
                    await this.setToken(newAccessToken, newRefreshToken, newExpiresIn);
                    return newAccessToken;
                } else {
                    return null;
                }
            } catch (error: unknown) {
                return null;
            }
        }
    }

}
